import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Btn, Label } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileSpreadsheet, Download, CheckCircle2, AlertCircle } from "lucide-react";

export function ImportDialog({ open, onClose, title, endpoint, templateName, columns, description, idPrefix, onDone, buttonLabel }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const close = () => { setFile(null); setResult(null); onClose(); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`${endpoint}/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = templateName; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const run = async () => {
    if (!file) { toast.error("Choose a .csv or .xlsx file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd);
      setResult(data);
      toast.success(`${data.created} imported${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      onDone?.(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${idPrefix}-import-dialog`}>
        <DialogHeader><DialogTitle className="font-head font-black uppercase flex items-center gap-2"><FileSpreadsheet size={18} className="text-[#FF5722]" /> {title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">{description}</p>
          <div className="border border-zinc-800 p-3 bg-black/30">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-1">Recognized columns</div>
            <div className="font-mono text-xs text-zinc-300">{columns}</div>
            <button data-testid={`${idPrefix}-download-template-btn`} onClick={downloadTemplate} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF5722] hover:underline"><Download size={14} /> Download template (.csv)</button>
          </div>
          <div>
            <Label>Choose file (.csv or .xlsx — exported from your CRM)</Label>
            <input data-testid={`${idPrefix}-import-file-input`} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="block w-full text-sm text-zinc-300 font-mono file:mr-3 file:py-2 file:px-3 file:border-2 file:border-black file:bg-[#FF5722] file:text-black file:font-bold file:uppercase file:text-xs file:cursor-pointer" />
            {file && <div className="mt-1 font-mono text-xs text-zinc-500 truncate">{file.name}</div>}
          </div>
          <Btn data-testid={`${idPrefix}-run-import-btn`} onClick={run} disabled={busy || !file} className="w-full">{busy ? "Importing…" : buttonLabel}</Btn>
          {result && (
            <div data-testid={`${idPrefix}-import-result`} className="space-y-2">
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="bg-green-900/40 border border-green-800 text-green-300 px-2 py-1">{result.created} imported</span>
                {"assigned" in result && <span className="bg-blue-900/40 border border-blue-800 text-blue-300 px-2 py-1">{result.assigned} assigned</span>}
                {"linked" in result && <span className="bg-blue-900/40 border border-blue-800 text-blue-300 px-2 py-1">{result.linked} linked to clients</span>}
                {result.skipped > 0 && <span className="bg-red-900/40 border border-red-800 text-red-300 px-2 py-1">{result.skipped} skipped</span>}
              </div>
              <div className="max-h-48 overflow-y-auto border border-zinc-800 divide-y divide-zinc-900">
                {result.results.map((r, i) => (
                  <div key={`${r.address}-${i}`} className="flex items-start gap-2 px-2 py-1.5 text-xs">
                    {r.status === "skipped" ? <AlertCircle size={13} className="text-red-400 mt-0.5 shrink-0" /> : <CheckCircle2 size={13} className="text-green-400 mt-0.5 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-white truncate">{r.address || "—"}</div>
                      <div className="font-mono text-[10px] uppercase text-zinc-500">{r.status}{r.reason ? ` · ${r.reason}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
